import {useCallback, useEffect, useRef, useState} from "react";
import {
    createCatalogEntity,
    deleteCatalogEntity,
    getCatalogEntity,
    getEfeitoFuncionalidade,
    getPendencias,
    listCatalogEntity,
    porAVenda,
    updateCatalogEntity
} from "./SistemasService";
import {extractListPayload, getBackendEntityMessage} from "./sistemasUtils";

// A API limita page_size a 100: a lista vem página a página até o total.
const TAMANHO_DA_PAGINA = 100;

export const listarTudo = async (entityKey, params) => {
    const itens = [];
    for (let page = 1; ; page += 1) {
        const response = await listCatalogEntity(entityKey, {...params, page, pageSize: TAMANHO_DA_PAGINA});
        const {items, total} = extractListPayload(response);
        itens.push(...items);
        if (!items.length || itens.length >= total) return itens;
    }
};

const valorDe = (response) => response?.data?.valor ?? response?.data ?? null;

const porNome = (a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR');

const funcionalidadesDo = async (moduloId) => (
    (await listarTudo('funcionalidades', {modulo_id: moduloId, sortBy: 'nome', order: 'asc'})).sort(porNome)
);

// O POST do catálogo não devolve o registro criado: o id sai da busca pela chave, que é única.
const idPelaChave = async (entityKey, chave, params = {}) => {
    const itens = await listarTudo(entityKey, {...params, chave});
    return itens.find((item) => item.chave === chave)?.id || null;
};

// O PUT do catálogo exige o `nome` e grava a `descricao` mesmo quando só outro campo muda: ausente, ela vira
// vazia (`update*` do app-funcionalidades). Os dois vão sempre com o valor atual; o resto ausente fica como está.
const comNomeEDescricao = (atual, campos) => ({nome: atual?.nome, descricao: atual?.descricao ?? null, ...campos});

/*
 * Estado e gravações da página única do sistema (BBF-174). Cada gravação vai na hora para a API, mesmo
 * incompleta; depois a página relê o que mudou e as pendências, que são calculadas pela API (BBF-173).
 * As funções gravam e lançam o erro da API para quem chamou mostrar onde a pessoa está.
 */
export const useSistemaPagina = (sistemaId) => {
    const [estado, setEstado] = useState({loading: true, erro: '', sistema: null, modulos: [], pendencias: null, metricas: null, erroDasMetricas: ''});
    const sistemaRef = useRef(null);
    sistemaRef.current = estado.sistema;

    // O cadastro de métricas (BBF-149), com as versões de plano que usam cada uma. Sem ele, a seção Métricas mostra
    // os apontamentos sem poder apontar outra.
    const recarregarMetricas = useCallback(async () => {
        try {
            const metricas = (await listarTudo('metricas', {sortBy: 'nome', order: 'asc'})).sort(porNome);
            setEstado((atual) => ({...atual, metricas, erroDasMetricas: ''}));
        } catch (error) {
            setEstado((atual) => ({...atual, erroDasMetricas: getBackendEntityMessage(error, 'Não foi possível carregar as métricas.')}));
        }
    }, []);

    const recarregarPendencias = useCallback(async () => {
        try {
            const pendencias = valorDe(await getPendencias(sistemaId));
            setEstado((atual) => ({...atual, pendencias}));
        } catch {
            // Sem as pendências, a página continua editável; a próxima gravação tenta de novo.
        }
    }, [sistemaId]);

    const carregar = useCallback(async () => {
        setEstado((atual) => ({...atual, loading: true, erro: ''}));
        try {
            const [sistemaResponse, modulos] = await Promise.all([
                getCatalogEntity('sistemas', sistemaId),
                listarTudo('modulos', {sistema_id: sistemaId, sortBy: 'nome', order: 'asc'}),
            ]);
            const comFuncionalidades = await Promise.all(modulos.sort(porNome).map(async (modulo) => ({
                ...modulo,
                funcionalidades: await funcionalidadesDo(modulo.id),
            })));
            setEstado((atual) => ({...atual, loading: false, erro: '', sistema: valorDe(sistemaResponse), modulos: comFuncionalidades}));
            recarregarPendencias();
            recarregarMetricas();
        } catch (error) {
            setEstado({loading: false, erro: getBackendEntityMessage(error, 'Não foi possível carregar o sistema.'), sistema: null, modulos: [], pendencias: null, metricas: null, erroDasMetricas: ''});
        }
    }, [recarregarMetricas, recarregarPendencias, sistemaId]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const recarregarSistema = useCallback(async () => {
        const sistema = valorDe(await getCatalogEntity('sistemas', sistemaId));
        setEstado((atual) => ({...atual, sistema}));
        return sistema;
    }, [sistemaId]);

    const recarregarFuncionalidades = useCallback(async (moduloIds) => {
        const listas = await Promise.all(moduloIds.map(async (id) => [id, await funcionalidadesDo(id)]));
        const porModulo = Object.fromEntries(listas);
        setEstado((atual) => ({
            ...atual,
            modulos: atual.modulos.map((modulo) => (porModulo[modulo.id] ? {...modulo, funcionalidades: porModulo[modulo.id]} : modulo)),
        }));
        return porModulo;
    }, []);

    /* ---------- sistema ---------- */

    const salvarSistema = useCallback(async (campos) => {
        await updateCatalogEntity('sistemas', sistemaId, comNomeEDescricao(sistemaRef.current, campos));
        await Promise.all([recarregarSistema(), recarregarPendencias()]);
    }, [recarregarPendencias, recarregarSistema, sistemaId]);

    const salvarIntegracao = useCallback((campos) => salvarSistema({integracao: campos}), [salvarSistema]);

    // A lista de apontamentos vai inteira: o que fica de fora deixa de estar apontado. Depois, o cadastro de métricas
    // é relido, porque tirar uma métrica tira o limite dela dos rascunhos do sistema.
    const salvarMetricas = useCallback(async (metricas) => {
        await salvarSistema({metricas});
        await recarregarMetricas();
    }, [recarregarMetricas, salvarSistema]);

    // 409 traz as pendências que bloqueiam em `valor`; quem chama mostra a lista.
    const porSistemaAVenda = useCallback(async () => {
        const resultado = valorDe(await porAVenda(sistemaId));
        await Promise.all([recarregarSistema(), recarregarPendencias()]);
        return resultado;
    }, [recarregarPendencias, recarregarSistema, sistemaId]);

    /* ---------- módulos ---------- */

    const criarModulo = useCallback(async ({nome, chave, descricao}) => {
        await createCatalogEntity('modulos', {nome, chave, descricao, sistema_id: sistemaId});
        const id = await idPelaChave('modulos', chave, {sistema_id: sistemaId});
        const modulo = id ? valorDe(await getCatalogEntity('modulos', id)) : null;
        if (modulo) {
            setEstado((atual) => ({...atual, modulos: [...atual.modulos, {...modulo, funcionalidades: []}].sort(porNome)}));
        }
        recarregarPendencias();
        return id;
    }, [recarregarPendencias, sistemaId]);

    const salvarModulo = useCallback(async (modulo, campos) => {
        await updateCatalogEntity('modulos', modulo.id, comNomeEDescricao(modulo, campos));
        setEstado((atual) => ({
            ...atual,
            modulos: atual.modulos.map((item) => (item.id === modulo.id ? {...item, ...campos} : item)).sort(porNome),
        }));
        recarregarPendencias();
    }, [recarregarPendencias]);

    const removerModulo = useCallback(async (moduloId) => {
        await deleteCatalogEntity('modulos', moduloId);
        setEstado((atual) => ({...atual, modulos: atual.modulos.filter((modulo) => modulo.id !== moduloId)}));
        recarregarPendencias();
    }, [recarregarPendencias]);

    /* ---------- funcionalidades e endpoints ---------- */

    const criarFuncionalidade = useCallback(async (moduloId, {nome, chave, tipo, recursos}) => {
        await createCatalogEntity('funcionalidades', {nome, chave, tipo, descricao: '', modulo_id: moduloId, recursos});
        const porModulo = await recarregarFuncionalidades([moduloId]);
        recarregarPendencias();
        return porModulo[moduloId]?.find((item) => item.chave === chave)?.id || null;
    }, [recarregarFuncionalidades, recarregarPendencias]);

    const salvarFuncionalidade = useCallback(async (funcionalidade, campos) => {
        await updateCatalogEntity('funcionalidades', funcionalidade.id, comNomeEDescricao(funcionalidade, campos));
        await recarregarFuncionalidades([funcionalidade.modulo_id]);
        recarregarPendencias();
    }, [recarregarFuncionalidades, recarregarPendencias]);

    const moverFuncionalidade = useCallback(async (funcionalidade, destinoId) => {
        await updateCatalogEntity('funcionalidades', funcionalidade.id, comNomeEDescricao(funcionalidade, {modulo_id: destinoId}));
        await recarregarFuncionalidades([funcionalidade.modulo_id, destinoId]);
        recarregarPendencias();
    }, [recarregarFuncionalidades, recarregarPendencias]);

    const removerFuncionalidade = useCallback(async (funcionalidade) => {
        await deleteCatalogEntity('funcionalidades', funcionalidade.id);
        await recarregarFuncionalidades([funcionalidade.modulo_id]);
        recarregarPendencias();
    }, [recarregarFuncionalidades, recarregarPendencias]);

    const efeito = useCallback(async (funcionalidade, destinoId) => (
        valorDe(await getEfeitoFuncionalidade(funcionalidade.id, destinoId))
    ), []);

    return {
        ...estado,
        recarregar: carregar,
        recarregarPendencias,
        salvarSistema,
        salvarIntegracao,
        salvarMetricas,
        porSistemaAVenda,
        criarModulo,
        salvarModulo,
        removerModulo,
        criarFuncionalidade,
        salvarFuncionalidade,
        moverFuncionalidade,
        removerFuncionalidade,
        efeito,
    };
};
